/**
 * Enhanced WebSocket Manager Service
 * Singleton service with improved connection stability and real-time agent tracking
 */

import {
  transformIncomingMessage,
  transformOutgoingMessage,
  extractPreparationData,
  isPreparationCompleteMessage,
  isPreparationFailedMessage
} from './websocketManager-patch';
import { API_CONFIG } from '../config/api-config';

// Type definition for preparation completed data
export interface PreparationCompletedData {
  insights?: {
    client_profile?: string;
    portfolio_analysis?: string;
    risk_assessment?: string;
    compliance_check?: string;
    interaction_analysis?: string;
    agenda_generation?: string;
    insight_synthesis?: string;
  };
  synthesis?: string;
  agenda?: string;
  processing_stats?: {
    chunks_processed: number;
    total_tokens: number;
    models_used: string[];
  };
}

export interface WebSocketCallbacks {
  onProgress: (progress: number, task: string, agent?: any) => void;
  onCompleted: (data: PreparationCompletedData) => void;
  onError: (error: string) => void;
  onAgentStatusUpdate?: (agentStatus: any) => void;
  onConnected?: () => void;
  onMeetingStatusUpdate?: (meetingUpdate: any) => void;
  onDashboardUpdate?: (dashboardData: any) => void;
  // Meeting-specific callbacks
  onTranscription?: (transcription: any) => void;
  onMeetingStarted?: (meetingData: any) => void;
  onClientContextUpdated?: (contextData: any) => void;
  onNotes?: (notes: any) => void;
  onSentiment?: (sentiment: any) => void;
  // New Stage-based callbacks for TASK-043
  onStageProgress?: (stageData: any) => void;
  onAIActivity?: (activityData: any) => void;
  onReasoningUpdate?: (reasoningData: any) => void;
  onDataSourceStatus?: (sourceStatus: any) => void;
}

export interface QueuedMessage {
  id: string;
  message: any;
  timestamp: Date;
  retryCount: number;
  maxRetries: number;
}

export interface ActiveSession {
  sessionId: string;
  meetingId: string;
  connection: WebSocket;
  callbacks: WebSocketCallbacks;
  reconnectAttempts: number;
  isReconnecting: boolean;
  preparationStatus: 'not_started' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  lastProgress: number;
  lastTask: string;
  agentStates?: any;
  sessionType: 'meeting' | 'dashboard';
  lastHealthCheck: Date;
  connectionId?: string;
  // Enhanced connection tracking
  lastPing: Date;
  lastPong: Date;
  pingInterval: number;
  pongTimeout: number;
  messageQueue: QueuedMessage[];
  connectionStats: {
    messagesReceived: number;
    messagesSent: number;
    reconnectionCount: number;
    lastReconnection: Date | null;
    averageLatency: number;
  };
}

class WebSocketManager {
  private static instance: WebSocketManager;
  private activeSessions: Map<string, ActiveSession> = new Map();
  private maxReconnectAttempts = 8; // Increased for better resilience
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private pingInterval = 30000; // 30 seconds (aligned with backend keepalive)
  private pongTimeout = 45000; // 45 seconds to wait for pong (increased for slow agent responses)
  private pingTimers: Map<string, NodeJS.Timeout> = new Map();
  private pongTimers: Map<string, NodeJS.Timeout> = new Map();
  private messageQueueProcessors: Map<string, NodeJS.Timeout> = new Map();
  
  private dashboardState: {
    meetings: any[];
    lastUpdate: Date;
    isConnected: boolean;
  } = {
    meetings: [],
    lastUpdate: new Date(),
    isConnected: false
  };
  private healthCheckInterval: NodeJS.Timeout | null = null;
  
  private constructor() {
    // Handle page visibility changes to manage connections
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // Handle before unload to clean up connections
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    
    // Handle online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
    
    console.log('🔌 Enhanced WebSocket Manager initialized');
  }
  
  static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }
  
  /**
   * Enhanced connection to meeting preparation session
   */
  connectToSession(
    sessionId: string,
    meetingId: string,
    callbacks: WebSocketCallbacks
  ): void {
    // Use meetingId as the primary identifier (session_id will be deprecated)
    // During transition, support both formats
    const primaryId = meetingId || sessionId;

    // Check if already connected to this session (check both IDs for backwards compatibility)
    const existingSession = this.activeSessions.get(primaryId) || this.activeSessions.get(sessionId);
    if (existingSession && existingSession.connection.readyState === WebSocket.OPEN) {
      console.log(`♻️ Reusing existing WebSocket connection for meeting ${primaryId}`);
      // Update callbacks for the new component
      existingSession.callbacks = callbacks;
      
      // Immediately send current state to new callbacks
      if (existingSession.sessionType === 'meeting') {
        if (existingSession.lastProgress > 0) {
          callbacks.onProgress(existingSession.lastProgress, existingSession.lastTask);
        }
        if (existingSession.agentStates) {
          callbacks.onAgentStatusUpdate?.(existingSession.agentStates);
        }
      } else if (existingSession.sessionType === 'dashboard') {
        // Send dashboard state to new callbacks
        callbacks.onDashboardUpdate?.({
          meetings: this.dashboardState.meetings,
          lastUpdate: this.dashboardState.lastUpdate,
          isConnected: this.dashboardState.isConnected
        });
      }
      
      // Notify that the connection is ready (important for reused connections)
      callbacks.onConnected?.();
      
      return;
    }
    
    // Pass primaryId instead of sessionId for consistency
    this.createNewConnection(primaryId, meetingId, callbacks);
  }
  
  /**
   * Create a new WebSocket connection with enhanced features
   */
  private createNewConnection(
    sessionId: string,
    meetingId: string,
    callbacks: WebSocketCallbacks
  ): void {
    // Determine session type and WebSocket URL
    const sessionType = sessionId === 'dashboard' ? 'dashboard' : 'meeting';
    // ✅ TASK-083: Use gateway WebSocket URL for account isolation and JWT authentication
    const baseWsUrl = `${API_CONFIG.WS_GATEWAY_URL}/ws`;

    // Add JWT token for authentication
    const token = localStorage.getItem('auth_token');
    const baseUrl = sessionType === 'dashboard'
      ? `${baseWsUrl}/dashboard`
      : `${baseWsUrl}/${sessionId}`;
    const wsUrl = token ? `${baseUrl}?token=${token}` : baseUrl;

    console.log(`🚀 Creating enhanced WebSocket connection for ${sessionType} session ${sessionId}`);
    console.log(`📡 WebSocket URL: ${wsUrl.replace(/token=[^&]+/, 'token=***')}`); // Mask token in logs

    const connection = new WebSocket(wsUrl);
    
    const session: ActiveSession = {
      sessionId,
      meetingId,
      connection,
      callbacks,
      reconnectAttempts: 0,
      isReconnecting: false,
      preparationStatus: 'not_started',
      lastProgress: 0,
      lastTask: 'Initializing...',
      sessionType,
      lastHealthCheck: new Date(),
      connectionId: `conn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      lastPing: new Date(),
      lastPong: new Date(),
      pingInterval: this.pingInterval,
      pongTimeout: this.pongTimeout,
      messageQueue: [],
      connectionStats: {
        messagesReceived: 0,
        messagesSent: 0,
        reconnectionCount: 0,
        lastReconnection: null,
        averageLatency: 0
      }
    };
    
    this.setupEnhancedConnectionHandlers(session);
    // Store using the primary ID (meetingId)
    this.activeSessions.set(sessionId, session);
    // Also store by meetingId if different for backwards compatibility
    if (meetingId && meetingId !== sessionId) {
      this.activeSessions.set(meetingId, session);
    }
    
    // Start health check timer if not already running
    if (!this.healthCheckInterval) {
      this.startHealthCheck();
    }
  }
  
  /**
   * Enhanced WebSocket event handlers with improved reliability
   */
  private setupEnhancedConnectionHandlers(session: ActiveSession): void {
    const { connection, callbacks, sessionId } = session;
    
    connection.onopen = () => {
      console.log(`✅ Enhanced WebSocket connected for session ${sessionId}`);
      session.reconnectAttempts = 0;
      session.isReconnecting = false;
      session.lastHealthCheck = new Date();
      session.lastPong = new Date();
      
      // Start enhanced ping timer
      this.startEnhancedPingTimer(sessionId);
      
      // Process any queued messages
      this.processMessageQueue(sessionId);
      
      // Request current state from server
      this.sendMessage(sessionId, { type: 'request_agent_status' });
      this.sendMessage(sessionId, { type: 'request_preparation_status' });
      
      // Notify connected callback
      callbacks.onConnected?.();
    };
    
    connection.onmessage = (event) => {
      try {
        const rawMessage = JSON.parse(event.data);
        // Transform incoming message for consolidated table compatibility
        const message = transformIncomingMessage(rawMessage);
        session.lastHealthCheck = new Date();
        session.connectionStats.messagesReceived++;
        
        // Handle ping/pong
        if (message.type === 'ping') {
          this.handlePing(sessionId, message);
          return;
        } else if (message.type === 'pong') {
          this.handlePong(sessionId, message);
          return;
        }
        
        this.handleEnhancedMessage(session, message);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
        callbacks.onError?.('Failed to parse server message');
      }
    };
    
    connection.onerror = (error) => {
      console.error(`❌ Enhanced WebSocket error for session ${sessionId}:`, error);
      callbacks.onError?.('WebSocket connection error');
    };
    
    connection.onclose = (event) => {
      console.log(`🔌 Enhanced WebSocket closed for session ${sessionId}:`, event.code, event.reason);
      
      // Stop ping timer
      this.stopEnhancedPingTimer(sessionId);
      
      // Attempt reconnection if not intentionally closed
      if (event.code !== 1000 && session.reconnectAttempts < this.maxReconnectAttempts) {
        this.attemptEnhancedReconnection(session);
      } else if (session.reconnectAttempts >= this.maxReconnectAttempts) {
        callbacks.onError?.('Failed to reconnect after multiple attempts');
        this.activeSessions.delete(sessionId);
        this.cleanupSession(sessionId);
      }
    };
  }
  
  /**
   * Enhanced message handling with better error recovery
   */
  private handleEnhancedMessage(session: ActiveSession, message: any): void {
    const { callbacks } = session;
    
    // Update last health check
    session.lastHealthCheck = new Date();
    
    // Extract preparation data from message
    const preparationData = extractPreparationData(message);
    if (preparationData.preparation_status) {
      session.preparationStatus = preparationData.preparation_status;
    }
    if (preparationData.preparation_progress !== undefined) {
      session.lastProgress = preparationData.preparation_progress;
    }
    
    // Check for completion or failure
    if (isPreparationCompleteMessage(message)) {
      session.preparationStatus = 'completed';
      callbacks.onCompleted?.(message.data || preparationData);
      callbacks.onProgress?.(100, 'Preparation completed', message.data);
      return;
    }
    
    if (isPreparationFailedMessage(message)) {
      session.preparationStatus = 'failed';
      callbacks.onError?.(message.error || message.preparation_error_message || 'Preparation failed');
      return;
    }
    
    try {
      switch (message.type) {
        case 'connection_established':
          console.log(`🔗 Connection established for session ${session.sessionId}:`, message.connection_id);
          session.connectionId = message.connection_id;
          break;
          
        case 'preparation_started':
          console.log(`🚀 Preparation started for session ${session.sessionId}:`, message.message);
          session.preparationStatus = 'in_progress';
          session.lastProgress = 0;
          callbacks.onProgress?.(0, 'Initializing preparation...', null);
          break;
          
        case 'job_progress':
          console.log(`📊 Job progress for session ${session.sessionId}:`, message);
          session.lastProgress = message.progress || 0;
          session.lastTask = message.message || 'Processing...';
          callbacks.onProgress?.(message.progress, message.message, message.metadata);
          break;
          
        case 'job_completed':
          console.log(`✅ Job completed for session ${session.sessionId}:`, message);
          session.preparationStatus = 'completed';
          if (message.data) {
            callbacks.onCompleted?.(message.data);
          }
          callbacks.onProgress?.(100, 'Preparation completed', message.data);
          break;
          
        case 'job_failed':
          console.log(`❌ Job failed for session ${session.sessionId}:`, message);
          session.preparationStatus = 'failed';
          callbacks.onError?.(message.error || 'Job processing failed');
          break;
          
        case 'preparation_progress':
          console.log('📊 Enhanced preparation_progress message received:', message);
          
          // Update session state
          session.lastProgress = message.progress || 0;
          session.lastTask = message.current_task || 'Processing...';
          session.preparationStatus = message.status || 'in_progress';
          
          // Store agent states for persistence across reconnections
          if (message.agents && Array.isArray(message.agents)) {
            session.agentStates = {
              session_id: message.session_id || session.sessionId,
              agents: message.agents,
              overall_status: message.overall_status || 'running',
              error_count: message.error_count || 0,
              warnings: message.warnings || [],
              timestamp: new Date()
            };
          }
          
          // Call enhanced progress callback with full context
          callbacks.onProgress(
            session.lastProgress, 
            session.lastTask, 
            message.agents || session.agentStates?.agents
          );
          
          // Also trigger agent status update if we have agent data
          if (session.agentStates && callbacks.onAgentStatusUpdate) {
            console.log('🔄 Triggering agent status update from preparation_progress');
            callbacks.onAgentStatusUpdate(session.agentStates);
          }
          
          // Handle completion detection within preparation_progress
          if (message.status === 'completed') {
            console.log('✅ Preparation completed detected in preparation_progress message');
            session.preparationStatus = 'completed';
            
            // If we have completion data, call the completion callback
            if (message.data || message.result) {
              callbacks.onCompleted(message.data || message.result);
            }
          } else if (message.status === 'failed') {
            console.log('❌ Preparation failed detected in preparation_progress message');
            session.preparationStatus = 'failed';
            callbacks.onError(message.message || message.error || 'Preparation failed');
          }
          
          break;
          
        case 'agent_status_update':
          console.log('🔄 WebSocket: agent_status_update received:', message);
          session.agentStates = {
            session_id: message.session_id,
            agents: message.agents,
            overall_status: message.overall_status,
            error_count: message.error_count || 0,
            warnings: message.warnings || []
          };
          console.log(`🤖 Enhanced agent status update for ${session.sessionId}:`, 
                     message.agents?.length || 0, 'agents,', message.overall_status);
          console.log('🔄 About to call onAgentStatusUpdate callback with:', session.agentStates);
          callbacks.onAgentStatusUpdate?.(session.agentStates);
          break;
          
        case 'preparation_completed':
          session.preparationStatus = 'completed';
          console.log(`✅ Preparation completed for session ${session.sessionId}`);
          callbacks.onCompleted(message.data);
          break;
          
        case 'preparation_failed':
          session.preparationStatus = 'failed';
          console.log(`❌ Preparation failed for session ${session.sessionId}:`, message.message);
          callbacks.onError(message.message || 'Preparation failed');
          break;
          
        case 'preparation_status_sync':
          session.preparationStatus = message.status;
          session.lastProgress = message.progress;
          session.lastTask = message.current_task;
          console.log(`🔄 Status sync for ${session.sessionId}: ${message.status} (${message.progress}%)`);
          if (message.progress > 0) {
            callbacks.onProgress(message.progress, message.current_task);
          }
          break;
          
        case 'error': {
          console.error(`❌ Server error for ${session.sessionId}:`, message.message);
          
          // Enhanced error handling with recovery options
          const errorMessage = message.message || 'Server error';
          
          // Specific handling for agent-related errors
          if (errorMessage.includes('agent') || errorMessage.includes('timeout')) {
            console.log(`🔄 Agent-related error detected, attempting recovery for ${session.sessionId}`);
            
            // Set a flag to attempt automatic recovery after a delay
            setTimeout(() => {
              if (session.connection.readyState === WebSocket.OPEN) {
                console.log(`🔄 Requesting agent status refresh for ${session.sessionId}`);
                this.sendMessage(session.sessionId, {
                  type: 'request_agent_status',
                  timestamp: new Date().toISOString()
                });
              }
            }, 5000); // Wait 5 seconds before recovery attempt
          }
          
          callbacks.onError?.(errorMessage);
          break;
        }
          
        case 'meeting_status_update':
          // Handle dashboard-level meeting status updates
          if (session.sessionType === 'dashboard') {
            const meetingUpdate = {
              meeting_id: message.meeting_id,
              session_id: message.session_id,
              preparation_status: message.preparation_status,
              preparation_progress: message.preparation_progress,
              timestamp: new Date()
            };
            
            this.updateDashboardMeetingStatus(meetingUpdate);
            callbacks.onMeetingStatusUpdate?.(meetingUpdate);
          }
          break;
          
        case 'dashboard_sync':
          if (session.sessionType === 'dashboard') {
            this.dashboardState.meetings = message.meetings || [];
            this.dashboardState.lastUpdate = new Date();
            this.dashboardState.isConnected = true;
            
            callbacks.onDashboardUpdate?.({
              meetings: this.dashboardState.meetings,
              lastUpdate: this.dashboardState.lastUpdate,
              isConnected: this.dashboardState.isConnected
            });
          }
          break;
          
        // Meeting-specific message types
        case 'transcription':
          console.log(`📝 Transcription received for session ${session.sessionId}`);
          callbacks.onTranscription?.(message.data);
          break;
          
        case 'meeting_started':
          console.log(`🚀 Meeting started for session ${session.sessionId}`);
          callbacks.onMeetingStarted?.(message);
          break;
          
        case 'client_context_updated':
          console.log(`👤 Client context updated for session ${session.sessionId}`);
          callbacks.onClientContextUpdated?.(message.data);
          break;
          
        case 'notes':
          console.log(`📋 Notes received for session ${session.sessionId}`);
          callbacks.onNotes?.(message.data);
          break;
          
        case 'sentiment':
          console.log(`💭 Sentiment analysis for session ${session.sessionId}`);
          callbacks.onSentiment?.(message.data);
          break;
          
        // Enhanced document analysis message types
        case 'document_discovery_started':
        case 'document_discovered':
        case 'document_discovery_completed':
        case 'document_analysis_started':
        case 'document_processed':
        case 'insight_generated':
          console.log('💡 New insight generated:', message);
          // Pass specific insight to callback
          if (callbacks.onAgentStatusUpdate) {
            callbacks.onAgentStatusUpdate({
              ...message,
              isInsight: true
            });
          }
          break;
          
        case 'insight_update':
        case 'insights_batch':
        case 'live_insights':
        case 'document_analysis_completed':
        case 'client_profile_analysis_started':
        case 'client_profile_section_completed':
        case 'client_recommendations_generated':
        case 'client_profile_completed':
        case 'relationship_analysis_progress':
        case 'insights_summary':
        case 'analysis_completed':
        case 'analysis_error':
        case 'document_discovery_error':
        case 'document_analysis_error':
        case 'client_profile_error':
        case 'agent_progress_update':
        case 'analysis_status_update':
          console.log(`🔄 Enhanced analysis message: ${message.type} for session ${session.sessionId}`);
          // Pass these messages through to the agent status update callback
          if (callbacks.onAgentStatusUpdate) {
            callbacks.onAgentStatusUpdate(message);
          }
          break;
          
        // WebSocket ready message from backend
        case 'websocket_ready':
          console.log(`✅ WebSocket ready confirmation for session ${session.sessionId}`);
          // This is just an acknowledgment, no action needed
          break;

        // New Stage-based progress messages for TASK-043
        case 'stage_progress':
          console.log(`🎯 Stage progress update for session ${session.sessionId}:`, message);
          if (callbacks.onStageProgress) {
            callbacks.onStageProgress(message);
          }
          break;

        case 'ai_activity':
          console.log(`🤖 AI activity update for session ${session.sessionId}:`, message);
          if (callbacks.onAIActivity) {
            callbacks.onAIActivity(message);
          }
          break;

        case 'reasoning_update':
          console.log(`💭 AI reasoning update for session ${session.sessionId}:`, message);
          if (callbacks.onReasoningUpdate) {
            callbacks.onReasoningUpdate(message);
          }
          break;

        case 'data_source_status':
          console.log(`📊 Data source status update for session ${session.sessionId}:`, message);
          if (callbacks.onDataSourceStatus) {
            callbacks.onDataSourceStatus(message);
          }
          break;

        // Data gathering messages for TASK-093
        case 'data_source_update':
          console.log(`📊 Data source update for session ${session.sessionId}:`, message);
          if (callbacks.onDataSourceStatus) {
            callbacks.onDataSourceStatus(message);
          }
          break;

        case 'gathering_complete':
          console.log(`✅ Data gathering completed for session ${session.sessionId}:`, message);
          if (callbacks.onDataSourceStatus) {
            callbacks.onDataSourceStatus(message);
          }
          break;

        default:
          console.log(`📨 Unknown message type for ${session.sessionId}:`, message.type);
          break;
      }
    } catch (error) {
      console.error(`Error handling message type ${message.type}:`, error);
      callbacks.onError?.(`Failed to process ${message.type} message`);
    }
  }
  
  /**
   * Enhanced reconnection with exponential backoff and circuit breaker
   */
  private attemptEnhancedReconnection(session: ActiveSession): void {
    if (session.isReconnecting) return;
    
    session.isReconnecting = true;
    session.reconnectAttempts++;
    session.connectionStats.reconnectionCount++;
    session.connectionStats.lastReconnection = new Date();
    
    // Calculate exponential backoff delay - more aggressive during agent execution
    const baseDelay = this.reconnectDelay;
    
    // Check if agents are running (faster reconnection during critical operations)
    const isAgentExecutionActive = session.agentStates?.overall_status === 'running' || 
                                  session.preparationStatus === 'in_progress';
    
    let exponentialDelay;
    if (isAgentExecutionActive && session.reconnectAttempts <= 3) {
      // Faster reconnection during agent execution (first 3 attempts)
      exponentialDelay = Math.min(baseDelay * session.reconnectAttempts, 5000); // Max 5s during agent execution
    } else {
      // Normal exponential backoff
      exponentialDelay = Math.min(
        baseDelay * Math.pow(2, session.reconnectAttempts - 1),
        this.maxReconnectDelay
      );
    }
    
    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 1000;
    const delay = exponentialDelay + jitter;
    
    console.log(`🔄 Enhanced reconnection attempt ${session.reconnectAttempts}/${this.maxReconnectAttempts} for ${session.sessionType} session ${session.sessionId} (delay: ${delay.toFixed(0)}ms)`);
    
    setTimeout(() => {
      if (!this.activeSessions.has(session.sessionId)) {
        console.log('Session no longer active, cancelling reconnection');
        return;
      }
      
      // Create new connection with proper URL based on session type
      // ✅ TASK-083: Use gateway WebSocket URL for account isolation and JWT authentication
      const baseWsUrl = `${API_CONFIG.WS_GATEWAY_URL}/ws`;

      // Add JWT token for authentication on reconnection
      const token = localStorage.getItem('auth_token');
      const baseUrl = session.sessionType === 'dashboard'
        ? `${baseWsUrl}/dashboard`
        : `${baseWsUrl}/${session.sessionId}`;
      const wsUrl = token ? `${baseUrl}?token=${token}` : baseUrl;

      console.log(`📡 Reconnection WebSocket URL: ${wsUrl.replace(/token=[^&]+/, 'token=***')}`); // Mask token in logs
      const newConnection = new WebSocket(wsUrl);
      
      // Update session with new connection
      session.connection = newConnection;
      session.connectionId = `conn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      this.setupEnhancedConnectionHandlers(session);
      
    }, delay);
  }
  
  /**
   * Get session state for resuming
   */
  getSessionState(sessionId: string): ActiveSession | undefined {
    return this.activeSessions.get(sessionId);
  }
  
  /**
   * Update callbacks for an existing session
   */
  updateSessionCallbacks(sessionId: string, callbacks: WebSocketCallbacks): void {
    const session = this.activeSessions.get(sessionId);
    if (session) {
      session.callbacks = callbacks;
      
      // Send current state to new callbacks
      if (session.lastProgress > 0) {
        callbacks.onProgress(session.lastProgress, session.lastTask);
      }
      if (session.agentStates) {
        callbacks.onAgentStatusUpdate?.(session.agentStates);
      }
    }
  }

  /**
   * Enhanced message sending with queuing and retry logic
   */
  sendMessage(sessionId: string, message: any): void {
    const session = this.activeSessions.get(sessionId);
    
    // Don't attempt to send if session doesn't exist
    if (!session) {
      console.error(`❌ Cannot send message to non-existent session ${sessionId}`);
      return;
    }
    
    // Determine message priority based on type
    const criticalMessageTypes = ['preparation_completed', 'agent_status_update', 'preparation_failed', 'preparation_progress'];
    const priority = criticalMessageTypes.includes(message.type) ? 'high' : 'normal';
    
    // Check connection state - must be OPEN and not in closing state
    if (session.connection.readyState === WebSocket.OPEN) {
      try {
        // Transform outgoing message for backward compatibility
        const transformedMessage = transformOutgoingMessage(message);
        session.connection.send(JSON.stringify(transformedMessage));
        session.connectionStats.messagesSent++;
        console.log(`📤 Enhanced message sent to ${sessionId}:`, message.type);
      } catch (error) {
        console.error(`❌ Error sending message to session ${sessionId}:`, error);
        // Only queue if it's not a closing error
        if (error instanceof Error && !error.message?.includes('close') && !error.message?.includes('CLOSED')) {
          this.queueMessage(sessionId, message, priority);
        }
      }
    } else {
      // Don't queue messages if connection is closing or closed, except for critical ones during reconnection
      const connectionState = session.connection.readyState;
      if ((connectionState === WebSocket.CONNECTING || session.isReconnecting) && priority === 'high') {
        console.log(`📥 Queueing critical message during reconnection for ${sessionId}:`, message.type);
        this.queueMessage(sessionId, message, priority);
      } else {
        console.warn(`⚠️ Dropping message for session ${sessionId} (state: ${connectionState}):`, message.type);
      }
    }
  }

  /**
   * Enhanced audio data sending with format optimization
   */
  sendAudioData(sessionId: string, audioBlob: Blob): void {
    const session = this.activeSessions.get(sessionId);
    if (session && session.connection.readyState === WebSocket.OPEN) {
      // Convert audio blob to base64 for transmission
      const reader = new FileReader()
      reader.onload = () => {
        const base64Audio = reader.result as string
        
        // Detect format from blob type
        const format = audioBlob.type.includes('wav') ? 'wav' : 'webm'
        
        console.log('📡 Sending enhanced audio via WebSocket:', audioBlob.size, 'bytes', format)
        this.sendMessage(sessionId, {
          type: 'audio_data',
          data: {
            audio: base64Audio.split(',')[1], // Remove data:audio/xxx;base64, prefix
            format: format,
            size: audioBlob.size,
            timestamp: new Date().toISOString()
          }
        })
      }
      reader.readAsDataURL(audioBlob)
    } else {
      console.warn(`⚠️ Cannot send audio to session ${sessionId}: WebSocket not connected`);
      // Queue message if WebSocket is not open
      this.queueMessage(sessionId, {
        type: 'audio_data',
        data: {
          audio: '', // No audio data to send if not connected
          format: 'webm', // Default format
          size: 0,
          timestamp: new Date().toISOString()
        }
      });
    }
  }

  /**
   * Enhanced disconnect with proper cleanup
   */
  disconnectSession(sessionId: string): void {
    const session = this.activeSessions.get(sessionId);
    if (session) {
      console.log(`🔌 Enhanced disconnect for session ${sessionId}`);
      
      // Clear any pending timers
      this.stopEnhancedPingTimer(sessionId);
      const pongTimer = this.pongTimers.get(sessionId);
      if (pongTimer) {
        clearTimeout(pongTimer);
        this.pongTimers.delete(sessionId);
      }
      
      const queueProcessor = this.messageQueueProcessors.get(sessionId);
      if (queueProcessor) {
        clearTimeout(queueProcessor);
        this.messageQueueProcessors.delete(sessionId);
      }
      
      // Close connection
      if (session.connection.readyState === WebSocket.OPEN || 
          session.connection.readyState === WebSocket.CONNECTING) {
        session.connection.close(1000, 'Manual disconnect');
      }
      
      // Remove from active sessions
      this.activeSessions.delete(sessionId);
      this.cleanupSession(sessionId);
    }
  }

  /**
   * Enhanced disconnect all with improved cleanup
   */
  disconnectAll(): void {
    console.log(`🔌 Enhanced disconnect all sessions (total: ${this.activeSessions.size})`);
    
    const sessionIds = Array.from(this.activeSessions.keys());
    sessionIds.forEach(sessionId => {
      this.disconnectSession(sessionId);
    });
    
    // Stop global health check
    this.stopHealthCheck();
  }

  /**
   * Enhanced ping timer with timeout handling
   */
  private startEnhancedPingTimer(sessionId: string): void {
    const timer = setInterval(() => {
      const session = this.activeSessions.get(sessionId);
      if (!session) {
        // Session no longer exists, stop the timer
        this.stopEnhancedPingTimer(sessionId);
        return;
      }
      
      // Only send ping if connection is OPEN and stable
      if (session.connection.readyState === WebSocket.OPEN && !session.isReconnecting) {
        const pingMessage = {
          type: 'ping',
          timestamp: new Date().toISOString(),
          connection_id: session.connectionId
        };
        
        try {
          session.connection.send(JSON.stringify(pingMessage));
          session.lastPing = new Date();
          
          // Start pong timeout timer
          const pongTimer = setTimeout(() => {
            console.warn(`⚠️ Pong timeout for session ${sessionId}`);
            // Consider connection dead and attempt reconnection only if still connected
            const currentSession = this.activeSessions.get(sessionId);
            if (currentSession && currentSession.connection.readyState === WebSocket.OPEN) {
              currentSession.connection.close(1006, 'Pong timeout');
            }
          }, this.pongTimeout);
          
          // Clear any existing pong timer
          const existingPongTimer = this.pongTimers.get(sessionId);
          if (existingPongTimer) {
            clearTimeout(existingPongTimer);
          }
          this.pongTimers.set(sessionId, pongTimer);
          
        } catch (error) {
          console.error(`❌ Failed to send ping to session ${sessionId}:`, error);
          // Stop ping timer if we can't send
          this.stopEnhancedPingTimer(sessionId);
        }
      } else {
        // Connection not ready, stop ping timer
        this.stopEnhancedPingTimer(sessionId);
      }
    }, this.pingInterval);
    
    this.pingTimers.set(sessionId, timer);
    console.log(`🏓 Enhanced ping timer started for session ${sessionId} (interval: ${this.pingInterval}ms)`);
  }

  /**
   * Enhanced ping timer stopping
   */
  private stopEnhancedPingTimer(sessionId: string): void {
    const timer = this.pingTimers.get(sessionId);
    if (timer) {
      clearInterval(timer);
      this.pingTimers.delete(sessionId);
      console.log(`🏓 Enhanced ping timer stopped for session ${sessionId}`);
    }
    
    // Also clear pong timer
    const pongTimer = this.pongTimers.get(sessionId);
    if (pongTimer) {
      clearTimeout(pongTimer);
      this.pongTimers.delete(sessionId);
    }
  }

  /**
   * Enhanced ping handling with latency calculation
   */
  private handlePing(sessionId: string, message: any): void {
    const session = this.activeSessions.get(sessionId);
    if (!session) {
      console.warn(`⚠️ Received ping for non-existent session ${sessionId}`);
      return;
    }
    
    // Only respond to ping if connection is OPEN and stable
    if (session.connection.readyState === WebSocket.OPEN && !session.isReconnecting) {
      const pongMessage = {
        type: 'pong',
        timestamp: new Date().toISOString(),
        connection_id: session.connectionId,
        ping_timestamp: message.timestamp
      };
      
      try {
        session.connection.send(JSON.stringify(pongMessage));
        console.log(`🏓 Enhanced ping received and pong sent for session ${sessionId}`);
      } catch (error) {
        console.error(`❌ Failed to send pong to session ${sessionId}:`, error);
      }
    } else {
      console.warn(`⚠️ Cannot send pong for session ${sessionId} (state: ${session.connection.readyState}, reconnecting: ${session.isReconnecting})`);
    }
  }

  /**
   * Enhanced pong handling with latency calculation
   */
  private handlePong(sessionId: string, message: any): void {
    const session = this.activeSessions.get(sessionId);
    if (session) {
      session.lastPong = new Date();
      
      // Calculate latency if timestamps are available
      if (message.ping_timestamp) {
        const pingTime = new Date(message.ping_timestamp);
        const latency = session.lastPong.getTime() - pingTime.getTime();
        
        // Update average latency using exponential moving average
        if (session.connectionStats.averageLatency === 0) {
          session.connectionStats.averageLatency = latency;
        } else {
          session.connectionStats.averageLatency = 
            (session.connectionStats.averageLatency * 0.8) + (latency * 0.2);
        }
        
        console.log(`🏓 Enhanced pong received for session ${sessionId} (latency: ${latency}ms, avg: ${session.connectionStats.averageLatency.toFixed(1)}ms)`);
      } else {
        console.log(`🏓 Enhanced pong received for session ${sessionId}`);
      }
      
      // Clear pong timeout timer
      const pongTimer = this.pongTimers.get(sessionId);
      if (pongTimer) {
        clearTimeout(pongTimer);
        this.pongTimers.delete(sessionId);
      }
    }
  }

  /**
   * Enhanced message queue processing with retry logic
   */
  private processMessageQueue(sessionId: string): void {
    const session = this.activeSessions.get(sessionId);
    if (!session || session.connection.readyState !== WebSocket.OPEN || session.isReconnecting) {
      return;
    }

    console.log(`📥 Processing message queue for ${sessionId} (${session.messageQueue.length} messages)`);

    const processNext = () => {
      // Re-check session state before each message
      const currentSession = this.activeSessions.get(sessionId);
      if (!currentSession || 
          currentSession.messageQueue.length === 0 || 
          currentSession.connection.readyState !== WebSocket.OPEN ||
          currentSession.isReconnecting) {
        return;
      }

      const queuedMessage = currentSession.messageQueue.shift();
      if (!queuedMessage) return;

      try {
        currentSession.connection.send(JSON.stringify(queuedMessage.message));
        currentSession.connectionStats.messagesSent++;
        console.log(`📤 Queued message sent for ${sessionId}:`, queuedMessage.message.type);
        
        // Process next message after a short delay
        setTimeout(processNext, 100);
        
      } catch (error) {
        console.error(`❌ Error sending queued message for ${sessionId}:`, error);
        
        // Don't retry if it's a connection error
        if (error instanceof Error && (error.message?.includes('close') || error.message?.includes('CLOSED'))) {
          console.log(`📥 Dropping queued message due to connection closure: ${queuedMessage.message.type}`);
          // Continue with next message (if any)
          setTimeout(processNext, 100);
          return;
        }
        
        // Retry logic for other errors
        if (queuedMessage.retryCount < queuedMessage.maxRetries) {
          queuedMessage.retryCount++;
          currentSession.messageQueue.unshift(queuedMessage); // Put back at front
          console.log(`🔄 Retrying queued message for ${sessionId} (attempt ${queuedMessage.retryCount}/${queuedMessage.maxRetries})`);
          
          // Retry after exponential backoff
          setTimeout(processNext, 1000 * Math.pow(2, queuedMessage.retryCount - 1));
        } else {
          console.error(`❌ Queued message failed permanently for ${sessionId}:`, queuedMessage.message.type);
          currentSession.callbacks.onError?.(`Failed to send ${queuedMessage.message.type} message after ${queuedMessage.maxRetries} retries`);
          
          // Continue with next message
          setTimeout(processNext, 100);
        }
      }
    };

    processNext();
  }

  /**
   * Enhanced message queuing with priority support
   */
  private queueMessage(sessionId: string, message: any, priority: 'high' | 'normal' = 'normal'): void {
    const session = this.activeSessions.get(sessionId);
    if (!session) {
      console.error(`❌ Cannot queue message for non-existent session ${sessionId}`);
      return;
    }

    const queuedMessage: QueuedMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      message: message,
      timestamp: new Date(),
      retryCount: 0,
      maxRetries: message.type === 'ping' ? 1 : 3 // Fewer retries for ping messages
    };

    // Add to queue based on priority
    if (priority === 'high') {
      session.messageQueue.unshift(queuedMessage); // Add to front
    } else {
      session.messageQueue.push(queuedMessage); // Add to back
    }

    console.log(`📥 Message queued for ${sessionId} (${priority} priority):`, message.type, `(queue size: ${session.messageQueue.length})`);

    // Limit queue size to prevent memory issues
    const maxQueueSize = 50;
    if (session.messageQueue.length > maxQueueSize) {
      const removed = session.messageQueue.splice(0, session.messageQueue.length - maxQueueSize);
      console.warn(`⚠️ Queue size limit reached for ${sessionId}, removed ${removed.length} old messages`);
    }
  }

  /**
   * Handle page visibility changes
   */
  private handleVisibilityChange(): void {
    if (document.hidden) {
      console.log('📱 Page hidden, maintaining WebSocket connections');
      // Connections stay open when page is hidden
    } else {
      console.log('📱 Page visible, checking WebSocket connections');
      // Check and reconnect any failed connections
      this.activeSessions.forEach((session) => {
        if (session.connection.readyState !== WebSocket.OPEN && 
            session.connection.readyState !== WebSocket.CONNECTING) {
          this.attemptEnhancedReconnection(session);
        }
      });
    }
  }
  
  /**
   * Handle online/offline events
   */
  private handleOnline(): void {
    console.log('📱 Online, checking WebSocket connections');
    this.activeSessions.forEach((session) => {
      if (session.connection.readyState === WebSocket.CLOSED || session.connection.readyState === WebSocket.CLOSING) {
        this.attemptEnhancedReconnection(session);
      }
    });
  }

  private handleOffline(): void {
    console.log('📱 Offline, maintaining WebSocket connections');
    // Connections stay open even if offline, but we might want to log a warning
  }

  /**
   * Handle before unload
   */
  private handleBeforeUnload(): void {
    // Only disconnect if explicitly leaving the application
    // Navigation within the app won't trigger this
    if (this.activeSessions.size > 0) {
      console.log('🚪 Page unloading, maintaining connections for potential return');
      // Don't disconnect - let connections timeout naturally if user doesn't return
    }
    
    // Stop health check timer
    this.stopHealthCheck();
  }
  
  /**
   * Check if a session is active
   */
  isSessionActive(sessionId: string): boolean {
    const session = this.activeSessions.get(sessionId);
    return session !== undefined && session.connection.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection status for a session
   */
  getConnectionStatus(sessionId: string): 'connecting' | 'connected' | 'disconnected' {
    const session = this.activeSessions.get(sessionId);
    if (!session) return 'disconnected';
    
    switch (session.connection.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      default:
        return 'disconnected';
    }
  }
  
  /**
   * Get all active sessions
   */
  getActiveSessions(): string[] {
    return Array.from(this.activeSessions.keys());
  }
  
  /**
   * Update dashboard meeting status
   */
  private updateDashboardMeetingStatus(meetingUpdate: any): void {
    const meetingIndex = this.dashboardState.meetings.findIndex(
      meeting => meeting.meeting_id === meetingUpdate.meeting_id
    );
    
    if (meetingIndex >= 0) {
      // Update existing meeting
      this.dashboardState.meetings[meetingIndex] = {
        ...this.dashboardState.meetings[meetingIndex],
        preparation_status: meetingUpdate.preparation_status,
        preparation_progress: meetingUpdate.preparation_progress,
        last_updated: meetingUpdate.timestamp
      };
    } else {
      // Add new meeting (shouldn't happen normally)
      console.warn(`Received update for unknown meeting ${meetingUpdate.meeting_id}`);
    }
    
    this.dashboardState.lastUpdate = new Date();
  }
  
  /**
   * Get dashboard state
   */
  getDashboardState(): typeof this.dashboardState {
    return { ...this.dashboardState };
  }
  
  /**
   * Start health check timer
   */
  private startHealthCheck(): void {
    this.healthCheckInterval = setInterval(() => {
      this.performHealthCheck();
    }, 60000); // Check every minute
  }
  
  /**
   * Perform health check on all connections
   */
  private performHealthCheck(): void {
    const now = new Date();
    const staleThreshold = 120000; // 2 minutes
    
    this.activeSessions.forEach((session, sessionId) => {
      const timeSinceLastCheck = now.getTime() - session.lastHealthCheck.getTime();
      
      if (timeSinceLastCheck > staleThreshold) {
        console.log(`⚠️ Health check failed for session ${sessionId}, attempting reconnection`);
        
        if (session.connection.readyState === WebSocket.OPEN) {
          // Connection appears open but hasn't responded - close and reconnect
          session.connection.close();
        } else {
          // Connection is already closed - attempt reconnection
          this.attemptEnhancedReconnection(session);
        }
      }
    });
  }
  
  /**
   * Stop health check timer
   */
  private stopHealthCheck(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  /**
   * Cleanup session resources
   */
  private cleanupSession(sessionId: string): void {
    const session = this.activeSessions.get(sessionId);
    if (session) {
      this.stopEnhancedPingTimer(sessionId);
      this.activeSessions.delete(sessionId);
      console.log(`🧹 Cleaned up session ${sessionId}`);
    }
  }
  
  /**
   * Check dashboard connection health
   */
  isDashboardConnected(): boolean {
    const dashboardSession = this.activeSessions.get('dashboard');
    return dashboardSession !== undefined && 
           dashboardSession.connection.readyState === WebSocket.OPEN &&
           this.dashboardState.isConnected;
  }
  
  /**
   * Force dashboard state refresh
   */
  refreshDashboardState(): void {
    const dashboardSession = this.activeSessions.get('dashboard');
    if (dashboardSession && dashboardSession.connection.readyState === WebSocket.OPEN) {
      dashboardSession.connection.send(JSON.stringify({
        type: 'request_dashboard_sync',
        timestamp: new Date().toISOString()
      }));
    }
  }

  /**
   * Get enhanced connection statistics
   */
  getConnectionStats(sessionId?: string) {
    if (sessionId) {
      const session = this.activeSessions.get(sessionId);
      return session ? {
        sessionId,
        connectionId: session.connectionId,
        isConnected: session.connection.readyState === WebSocket.OPEN,
        reconnectAttempts: session.reconnectAttempts,
        lastPing: session.lastPing,
        lastPong: session.lastPong,
        queueSize: session.messageQueue.length,
        ...session.connectionStats
      } : null;
    }

    return {
      totalSessions: this.activeSessions.size,
      activeSessions: Array.from(this.activeSessions.entries()).map(([id, session]) => ({
        sessionId: id,
        connectionId: session.connectionId,
        isConnected: session.connection.readyState === WebSocket.OPEN,
        reconnectAttempts: session.reconnectAttempts,
        queueSize: session.messageQueue.length,
        ...session.connectionStats
      }))
    };
  }
}

// Export singleton instance
export const websocketManager = WebSocketManager.getInstance();
